<template>
<div class="users">
    <div class="page__header">
        <div class="page__title">{{ $t(`pageTitles.${"users"}`) }}</div>
        <div class="page__right">
            <div class="selected-filters" v-if="selectedTeam && selectedTeam.length > 0">
                <div  
                    class="selected-filters__filter" 
                    v-for="teamId in selectedTeam" 
                    :key="`team-${teamId}`"
                >
                    <span>Team: {{ getTeamName(teamId) }}</span>
                    <div class="selected-filters__remove" @click="removeTeamTag(teamId)"></div>
                </div>

                <div class="selected-filters__filter" v-if="selectedStatus !== null">
                    <span>Status: {{ selectedStatus == 1 ? 'Active' : 'Inactive' }}</span>
                    <div class="selected-filters__remove" @click="removeStatusTag()"></div>
                </div>
            </div> 
            <form class="search search--filter" @submit.prevent="applyFilters()">
                <div class="search__wrapper">
                    <input class="search__search" :class="{ 'search--active': searchKeywords.length }" type="text" v-model="searchKeywords" />
                    <div class="search__icon"></div>
                    <div class="search__splitter"></div>
                </div>
                <div class="search__filter" ref="filterToggler" @click="showSearchFilter = !showSearchFilter"></div>
                <SearchFilter v-if="showSearchFilter" :filterToggler="$refs.filterToggler" :searchType="'user'" :toggleSearchFilter="
                (state) => {
                  showSearchFilter = state;
                }
              " />
            </form>

            <button v-if="$checkRolePermission('user_create')" class="users__button button button--default" @click="$store.commit('setShowAddUserModal', true)">
                {{ $t(`settings.user.${"addUserBtn"}`) }}
            </button>
        </div>
    </div>

    <div class="table">
        <Loading v-if="usersLoading" />
        <template v-else>
            <NoData v-if="!usersPerPage.data.length" :type="'users'" />
            <template v-else>
                <div class="table__header box box--default">
                    <div class="table__label table__label--small"></div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"name"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"contact"}`) }}
                    </div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"team"}`) }}
                    </div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"companyName"}`) }}
                    </div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"address"}`) }}
                    </div>
                    <div class="table__label table__label--small">
                        {{ $t(`users.${"tableHeader"}.${"zipCode"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"city"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"supervisor"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"salesGroup"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"account"}`) }}
                    </div>
                    <div class="table__label table__label--small" v-if="$checkRolePermission('user_delete')"></div>
                </div>

                <div class="table__body">
                    <User v-for="user in usersPerPage.data" :key="user.id" :user="user" :deleteUser="deleteUser" />
                </div>
            </template>
        </template>
    </div>
    <Pagination :limit="3" :data="usersPerPage" @pagination-change-page="getResults"></Pagination>
</div>
</template>

<script>
import {
    mapGetters
} from "vuex";
import User from "@/components/Users/User";
import Loading from "@/partials/Loading";
import NoData from "@/partials/NoData";
import SearchFilter from "@/partials/SearchFilter";
export default {
    name: "Users",

    components: {
        User,
        Loading,
        NoData,
        SearchFilter,
    },

    data() {
        return {
            searchKeywords: "",
            showSearchFilter: false,
            selectedTeam: null,
            selectedStatus: null,
            initialLoadDone: false
        };
    },

    watch: {
        "$route.query": {
            async handler(query, _oldQuery) { 

                if (!query.page) {
                    this.$router.replace({
                        query: { ...query, page: 1 },
                    });
                    return;
                }

                if (_oldQuery && JSON.stringify(query) === JSON.stringify(_oldQuery)) {
                    return;
                }

                this.syncFiltersFromRoute(query);

                await this.searchUsers();

                this.initialLoadDone = true;
            },
            immediate: true,
            deep: true,
        },
    },

    computed: {
        ...mapGetters(["usersLoading", "rolePermissions", "usersPerPage", "loggedUser", ]),
    },

    async created() {
        if (!this.$checkRolePermission('user_access')) {
            this.$router.push('/dashboard');
        }
    },

    methods: { 
        syncFiltersFromRoute(query) { 
            this.searchKeywords = query.search || "";
            
            if (query.teams) {
                const teams = Array.isArray(query.teams) ? query.teams : String(query.teams).split(",");
                this.selectedTeam = teams.map(id => Number(id));
            } else {
                this.selectedTeam = [];
            }

            this.selectedStatus = (query.active_users !== undefined && query.active_users !== "") ? query.active_users : null;
        },

        getTeamName(id) {
            if (this.loggedUser && this.loggedUser.teams) { 
                const team = this.loggedUser.teams.find(t => t.id === id);
                return team ? team.name : `Team ${id}`;
            } else {
                return;
            }
        },

        removeTeamTag(id) {
            this.selectedTeam = this.selectedTeam.filter(teamId => teamId !== id);
            this.applyFilters();
        },

        removeStatusTag() {
            this.selectedStatus = null;
            this.applyFilters();
        },

        applyFilters() {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    page: 1,
                    search: this.searchKeywords || undefined,
                    teams: this.selectedTeam.length ? this.selectedTeam.join(",") : undefined,
                    active_users: this.selectedStatus !== null ? this.selectedStatus : undefined
                }
            });
        },

        deleteUser({
            display,
            message,
            action,
            id
        }) {
            this.$store.commit("setDeleteModal", {
                display,
                message,
                action,
                id,
            });
        },

        async searchUsers() {
           this.$store.commit("setUsersLoading", true);
            try {
                const q = this.$route.query;
                const page = Number(q.page) || 1;
    
                const hasFilters = q.search || q.teams || (q.active_users !== undefined && q.active_users !== null);

                if (hasFilters) {
                    await this.$store.dispatch("searchUsers", {
                        searchKeywords: q.search || "",
                        selectedTeam: q.teams ? q.teams.split(",").map(Number) : [],
                        active_users: q.active_users,
                        page,
                    });
                } else {
                    await this.$store.dispatch("getUsersPage", { page });
                }
            } finally {
                this.$store.commit("setUsersLoading", false);
            }
        },

        async getResults(paginationPage) {
            const currentPage = Number(this.$route.query.page) || 1;
            if (Number(paginationPage) === currentPage) return;

            this.$router.replace({
                query: { ...this.$route.query, page: paginationPage },
            }); 
        },
    },
};
</script>

<style lang="scss" scoped>
.users {
    height: 100%;

    &__button {
        width: 190px;
    }

    .table {
        margin-bottom: 20px;
        height: calc(100% - 40px - 20px - 20px - 30px);
    }
}

.search-filter {
    max-width: 600px !important;
}
</style>
